import { ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";

import { Badge, Button, Label, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import { InlineEditField } from "@components/ProgrammingReview/InlineEditField";

interface InlineEditFieldWithAIProps<T extends Record<string, unknown>> {
  value: string | null | undefined;
  fieldKey: keyof T;
  onSave: (fields: Partial<T>) => Promise<{ success: boolean; error?: string }>;
  title: string;
  isMultiline?: boolean;
  required?: boolean;
  inputType?: "text" | "date" | "select";
  selectOptions?: SelectItem[];
  isReportCompleted?: boolean;
  transformBeforeSave?: (value: string) => T[keyof T];
  isGenerating: boolean;
  isGenerated: boolean;
  isError: boolean;
  onRegenerate?: () => void;
  subTitle?: string;
  canRegenerate?: boolean;
  errorMessage?: string;
  suggestionFieldId?: string;
  suggestionCount?: number;
}

export const InlineEditFieldWithAI = <T extends Record<string, unknown>>({
  value,
  fieldKey,
  onSave,
  title,
  isMultiline = false,
  required = false,
  inputType = "text",
  selectOptions = [],
  isReportCompleted = false,
  transformBeforeSave,
  isGenerating,
  isGenerated,
  isError,
  onRegenerate,
  subTitle,
  canRegenerate = true,
  errorMessage = "AI generation failed",
  suggestionFieldId,
  suggestionCount,
}: InlineEditFieldWithAIProps<T>) => {
  const renderStatusLabel = () => {
    if (isError) {
      return (
        <div className="flex items-center space-x-2 text-globemallow-600">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <span className="text-sm">{errorMessage}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Badge text="AI" appearance="ai" className={isGenerating ? "" : "animate-none!"} />
        {subTitle && <Small>{subTitle}</Small>}
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className="mb-1">
        <Label size="medium" colorType="primary">
          {title}
        </Label>
        <div className="flex items-center justify-between">
          {renderStatusLabel()}

          {canRegenerate && !isReportCompleted && (
            <Button
              text={
                <div className="flex items-center">
                  <SparklesIcon className="w-5 h-5" /> &nbsp;
                  {isGenerating ? "Generating..." : isGenerated ? "Regenerate" : "Generate"}
                </div>
              }
              appearance="text"
              className="p-0 text-green-700 hover:bg-white [&]:border-green-700! [&:hover]:border-green-700! focus:border-green-700! active:border-green-700!"
              type="button"
              style={{ fontWeight: 400, padding: 0, height: "auto" }}
              onClick={onRegenerate}
              disabled={isGenerating}
            />
          )}
        </div>
      </div>
    );
  };

  const renderLoadingSkeleton = () => {
    return (
      <div className="animate-pulse py-2 mb-2">
        <div className="bg-limestone-200 h-40 rounded-lg"></div>
      </div>
    );
  };

  return (
    <div>
      {renderHeader()}
      {isGenerating ? (
        renderLoadingSkeleton()
      ) : suggestionFieldId !== undefined && suggestionCount !== undefined ? (
        <InlineEditField
          value={value}
          fieldKey={fieldKey}
          onSave={onSave}
          title=""
          isMultiline={isMultiline}
          required={required}
          inputType={inputType}
          selectOptions={selectOptions}
          isReportCompleted={isReportCompleted}
          transformBeforeSave={transformBeforeSave}
          suggestionFieldId={suggestionFieldId}
          suggestionCount={suggestionCount}
        />
      ) : (
        <InlineEditField
          value={value}
          fieldKey={fieldKey}
          onSave={onSave}
          title=""
          isMultiline={isMultiline}
          required={required}
          inputType={inputType}
          selectOptions={selectOptions}
          isReportCompleted={isReportCompleted}
          transformBeforeSave={transformBeforeSave}
        />
      )}
    </div>
  );
};
